import { useLocation, useParams } from "react-router";

/**
 * Hook to get the customerId from the URL (optional).
 * Returns `string | undefined` - can be undefined if not on a customer-specific route.
 *
 * **When to use:**
 * - In components that appear on BOTH customer-specific and non-customer routes
 * - When customerId is optional and you need to handle both cases
 * - In global navigation components (headers, menus) that need to adapt based on context
 *
 * **Examples from codebase:**
 * - Header.tsx - Shows different navigation based on whether user is in customer context
 * - CustomerNavLinks.tsx - Conditionally renders customer-specific links
 * - use-secondary-menu-items.ts - Builds different menu items based on customer context
 * - ExternalUserCustomerMenu.tsx - Optional customer selection in menu
 * - InternalUserCustomerMenu.tsx - Shows customer menu only when customerId exists
 *
 * **Usage pattern:**
 * ```typescript
 * const customerId = useCustomerId();
 * if (customerId) {
 *   // Render customer-specific content
 *   return <Link to={`/customers/${customerId}/dashboard`}>Dashboard</Link>;
 * } else {
 *   // Render generic content
 *   return <Link to="/customers/my">My Customers</Link>;
 * }
 * ```
 */
export const useCustomerId = (): string | undefined => {
  const { customerId } = useParams<{ customerId: string }>();
  const location = useLocation();

  // If useParams has customerId, return it (component is inside route context)
  if (customerId) {
    return customerId;
  }

  // Otherwise, try to extract from pathname (component is outside route context)
  const match = location.pathname.match(/\/customers\/([^/]+)/);
  return match ? match[1] : undefined;
};

/**
 * Hook to get the customerId from the URL (required).
 * Returns `string` - throws an error if customerId is not found in the URL.
 *
 * **When to use:**
 * - In components that ONLY appear on customer-specific routes
 * - When the component absolutely requires a customerId to function
 * - In customer-specific views where missing customerId indicates routing error
 *
 * **Examples from codebase:**
 * - CustomerLayout.tsx - Layout wrapper for all customer routes
 * - Dashboard.tsx - Customer dashboard (route: /customers/:customerId/dashboard)
 * - DocumentDelivery.tsx - Customer delivery page (route: /customers/:customerId/delivery)
 * - DocumentArchive.tsx - Customer archive page (route: /customers/:customerId/archive)
 * - InventoryInternalUser.tsx - Customer inventory (route: /customers/:customerId/inventory)
 * - CustomerSettings.tsx - Customer settings (route: /customers/:customerId/settings)
 * - Inbox.tsx - Customer inbox (route: /customers/:customerId/inbox or /inbox)
 * - InboxSidebar components - All inbox-related components that need customerId
 *
 * **Usage pattern:**
 * ```typescript
 * const customerId = useRequiredCustomerId();
 * // No need for null checks - customerId is guaranteed to be a string
 * const { data } = useCustomerData(customerId);
 * return <CustomerDashboard customerId={customerId} data={data} />;
 * ```
 *
 * **Error handling:**
 * If this hook is used outside a customer-specific route, it will throw an error:
 * "CustomerId is required but not found in URL"
 * This helps catch routing/component placement errors during development.
 */
export const useRequiredCustomerId = (): string => {
  const customerId = useCustomerId();
  if (!customerId) {
    throw new Error("CustomerId is required but not found in URL");
  }
  return customerId;
};
